import { useMemo, useState } from "react";
import { Eye, Loader2, Pencil, Plus, Printer, Trash2 } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Button } from "@akuna/ui";
import { Switch } from "@akuna/ui";
import { Badge } from "@/components/ui/badge";
import { ConfirmationDialog } from "@/components/ui/confirmation-dialog";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  useCreateTermTemplate,
  useDeleteTermTemplate,
  useTermTemplates,
  useUpdateTermTemplate,
  type TermTemplate,
} from "@/hooks/usePatientTerms";
import { RichTextEditor } from "@/components/terms/RichTextEditor";
import { TermPrintPreview } from "@/components/terms/TermPrintPreview";
import {
  countRichTextPlaceholderOccurrences,
  extractRichTextPlaceholders,
  stripRichTextHtml,
} from "@/components/terms/rich-text-utils";
import {
  DEFAULT_TERM_TEMPLATE_CONFIG,
  getDefaultTermTemplateContent,
  getTermTokenDefinitions,
  getTermTokensByCategory,
  normalizeTermTemplateConfig,
  renderTermWithTokenValues,
  type TermTemplateConfig,
} from "@/lib/terms/config";
import { useTranslation } from "react-i18next";

type FormState = {
  name: string;
  content: string;
  is_active: boolean;
  config: TermTemplateConfig;
};

const initialForm: FormState = {
  name: "",
  content: "",
  is_active: true,
  config: DEFAULT_TERM_TEMPLATE_CONFIG,
};

const printViaIframe = (html: string) => {
  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.width = "0";
  iframe.style.height = "0";
  iframe.style.border = "0";
  iframe.style.right = "0";
  iframe.style.bottom = "0";
  document.body.appendChild(iframe);

  const frameWindow = iframe.contentWindow;
  const frameDocument = frameWindow?.document;

  if (!frameWindow || !frameDocument) {
    iframe.remove();
    return;
  }

  frameDocument.open();
  frameDocument.write(html);
  frameDocument.close();

  const cleanup = () => {
    if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
  };

  frameWindow.onafterprint = cleanup;

  setTimeout(() => {
    frameWindow.focus();
    frameWindow.print();
  }, 120);

  setTimeout(cleanup, 30000);
};

const SIGNATURE_LIMITED_KEYS = ["ASSINATURA_RESPONSAVEL", "ASSINATURA_VETERINARIO"];

const buildPreviewValues = (tokenDefinitions: { key: string }[]) => {
  const values: Record<string, string> = {};
  tokenDefinitions.forEach((item) => {
    values[item.key] = `{${item.key}}`;
  });
  return values;
};

type TokenPanelProps = {
  onInsertToken: (token: string) => void;
  categories: ReturnType<typeof getTermTokensByCategory>;
  disabledTokenKeys: Set<string>;
};

const TokenPanel = ({ onInsertToken, categories, disabledTokenKeys }: TokenPanelProps) => {
  const { t } = useTranslation("terms");
  return (
    <div>
      <p className="text-sm font-medium leading-none">{t("tokenPanel.availableLabels")}</p>
      <Accordion type="single" collapsible defaultValue="animal" className="w-full rounded-lg border mt-2">
        {Object.entries(categories).map(([category, tokens]) => (
          <AccordionItem key={category} value={category} className="border-b last:border-b-0">
            <AccordionTrigger className="px-4">{t(`tokenCategories.${category}`)}</AccordionTrigger>
            <AccordionContent className="px-4 pb-4">
              {category === "signature" ? (
                <p className="mb-2 text-xs text-muted-foreground">{t("tokenPanel.signatureLimitHint")}</p>
              ) : null}
              <div className="space-y-2">
                {tokens.map((token) => {
                  const tokenValue = `{${token.key}}`;
                  const isDisabled = disabledTokenKeys.has(token.key);
                  return (
                    <button
                      key={token.key}
                      type="button"
                      className="block w-full border rounded px-2 py-1 text-left font-mono text-sm hover:text-primary hover:bg-secondary hover:border-secondary disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-transparent disabled:hover:text-inherit disabled:hover:border-inherit"
                      onClick={() => onInsertToken(tokenValue)}
                      disabled={isDisabled}
                      title={
                        isDisabled
                          ? t("tokenPanel.signatureAlreadyUsed")
                          : t("tokenPanel.insertTokenTitle", { token: tokenValue })
                      }
                    >
                      {tokenValue}
                    </button>
                  );
                })}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </div>
  );
};

export const TermTemplatesTab = () => {
  const { t } = useTranslation("terms");
  const defaultTemplateContent = useMemo(() => getDefaultTermTemplateContent(t), [t]);
  const tokenDefinitions = useMemo(() => getTermTokenDefinitions(t), [t]);
  const tokenCategories = useMemo(() => getTermTokensByCategory(t), [t]);
  const tokenKeySet = useMemo(() => new Set(tokenDefinitions.map((item) => item.key)), [tokenDefinitions]);
  const { data: models = [], isLoading } = useTermTemplates(false);
  const createModel = useCreateTermTemplate();
  const updateModel = useUpdateTermTemplate();
  const deleteModel = useDeleteTermTemplate();

  const [form, setForm] = useState<FormState>({ ...initialForm, content: defaultTemplateContent });
  const [modelToDelete, setModelToDelete] = useState<TermTemplate | null>(null);
  const [editingModel, setEditingModel] = useState<TermTemplate | null>(null);
  const [previewModel, setPreviewModel] = useState<TermTemplate | null>(null);
  const [editForm, setEditForm] = useState<FormState>({ ...initialForm, content: defaultTemplateContent });
  const [createInsertRequest, setCreateInsertRequest] = useState<{ id: string; text: string } | null>(null);
  const [editInsertRequest, setEditInsertRequest] = useState<{ id: string; text: string } | null>(null);

  const isSavingNew = createModel.isPending;
  const isSavingEdit = updateModel.isPending;

  const sortedModels = useMemo(
    () => [...models].sort((a, b) => a.name.localeCompare(b.name, "pt-BR")),
    [models],
  );

  const createUnknownTokens = useMemo(() => {
    const keys = extractRichTextPlaceholders(`${form.name} ${form.content}`);
    return keys.filter((key) => !tokenKeySet.has(key));
  }, [form.name, form.content, tokenKeySet]);

  const editUnknownTokens = useMemo(() => {
    const keys = extractRichTextPlaceholders(`${editForm.name} ${editForm.content}`);
    return keys.filter((key) => !tokenKeySet.has(key));
  }, [editForm.name, editForm.content, tokenKeySet]);

  const createDisabledSignatureKeys = useMemo(() => {
    const disabled = new Set<string>();
    SIGNATURE_LIMITED_KEYS.forEach((key) => {
      if (countRichTextPlaceholderOccurrences(form.content, key) >= 1) disabled.add(key);
    });
    return disabled;
  }, [form.content]);

  const editDisabledSignatureKeys = useMemo(() => {
    const disabled = new Set<string>();
    SIGNATURE_LIMITED_KEYS.forEach((key) => {
      if (countRichTextPlaceholderOccurrences(editForm.content, key) >= 1) disabled.add(key);
    });
    return disabled;
  }, [editForm.content]);

  const handleUseDefaultTemplateEdit = () => {
    setEditForm((prev) => ({
      ...prev,
      name: prev.name.trim() ? prev.name : "",
      content: prev.content.trim() ? prev.content : defaultTemplateContent,
    }));
  };

  const resetCreateForm = () => {
    setForm({ ...initialForm, content: defaultTemplateContent });
  };

  const handleCreateModel = async () => {
    if (!form.name.trim() || !stripRichTextHtml(form.content).trim()) return;
    if (createUnknownTokens.length > 0) return;

    await createModel.mutateAsync({
      name: form.name,
      content: form.content,
      is_active: form.is_active,
      config_json: normalizeTermTemplateConfig(form.config),
    });

    resetCreateForm();
  };

  const handleOpenEdit = (model: TermTemplate) => {
    setEditingModel(model);
    setEditForm({
      name: model.name ?? "",
      content: model.content ?? "",
      is_active: Boolean(model.is_active),
      config: normalizeTermTemplateConfig(model.config_json),
    });
  };

  const handleSaveEdit = async () => {
    if (!editingModel?.id) return;
    if (!editForm.name.trim() || !stripRichTextHtml(editForm.content).trim()) return;
    if (editUnknownTokens.length > 0) return;

    await updateModel.mutateAsync({
      templateId: editingModel.id,
      payload: {
        name: editForm.name,
        content: editForm.content,
        is_active: editForm.is_active,
        config_json: normalizeTermTemplateConfig(editForm.config),
      },
    });

    setEditingModel(null);
    setEditForm({ ...initialForm, content: defaultTemplateContent });
  };

  const renderedPreview = useMemo(() => {
    if (!previewModel) return null;
    const values = buildPreviewValues(tokenDefinitions);
    const title = renderTermWithTokenValues(previewModel.name, values);
    const content = renderTermWithTokenValues(previewModel.content, values);
    return {
      title,
      content,
    };
  }, [previewModel, tokenDefinitions]);

  return (
    <div className="max-w-full space-y-4 sm:space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>{t("templates.newModel")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="term-model-name">{t("templates.modelName")}</Label>
            <Input
              id="term-model-name"
              placeholder={t("templates.modelNamePlaceholder")}
              value={form.name}
              onChange={(event) => setForm((prev) => ({ ...prev, name: event.target.value }))}
            />
          </div>


          <div className="grid max-w-full gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
            <div className="min-w-0 space-y-2">
              <div className="flex items-center justify-between">
                <Label>{t("templates.modelContent")}</Label>
              </div>
              <RichTextEditor
                value={form.content}
                onChange={(content) => setForm((prev) => ({ ...prev, content }))}
                placeholder={t("templates.modelContentPlaceholder")}
                insertTextRequest={createInsertRequest}
                onInsertTextApplied={() => setCreateInsertRequest(null)}
              />
              <p className="text-xs text-muted-foreground">
                {t("templates.paperHint")}
              </p>
            </div>

            <TokenPanel
              onInsertToken={(token) =>
                setCreateInsertRequest({ id: `${Date.now()}`, text: token })
              }
              categories={tokenCategories}
              disabledTokenKeys={createDisabledSignatureKeys}
            />
          </div>

          {createUnknownTokens.length > 0 ? (
            <p className="text-xs text-destructive">
              {t("templates.invalidTokens", { tokens: createUnknownTokens.map((token) => `{${token}}`).join(", ") })}
            </p>
          ) : null}

          <div className="flex items-center justify-between rounded-lg border p-3">
            <div>
              <p className="text-sm font-medium">{t("templates.activeLabel")}</p>
              <p className="text-xs text-muted-foreground">{t("templates.activeHint")}</p>
            </div>
            <Switch
              checked={form.is_active}
              onCheckedChange={(checked) => setForm((prev) => ({ ...prev, is_active: checked }))}
            />
          </div>

          <div className="flex flex-col gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={resetCreateForm}
              disabled={isSavingNew}
            >
              {t("templates.actions.clear")}
            </Button>
            <Button
              type="button"
              className="w-full"
              onClick={handleCreateModel}
              disabled={
                isSavingNew ||
                !form.name.trim() ||
                !stripRichTextHtml(form.content).trim() ||
                createUnknownTokens.length > 0
              }
            >
              {isSavingNew ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {t("templates.actions.saving")}
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  {t("templates.actions.create")}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>{t("templates.createdModels")}</CardTitle>
          <CardDescription>
            {t("templates.createdModelsDescription")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className="flex min-h-[180px] items-center justify-center">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : sortedModels.length === 0 ? (
            <p className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
              {t("templates.empty")}
            </p>
          ) : (
            <div className="space-y-3">
              {sortedModels.map((model) => (
                <div key={model.id} className="rounded-lg border p-3">
                  <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="flex items-center gap-2">
                      <p className="truncate font-medium">{model.name}</p>
                      <Badge variant={model.is_active ? "default" : "outline"}>
                        {model.is_active ? t("templates.status.active") : t("templates.status.inactive")}
                      </Badge>
                    </div>

                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t("templates.actions.previewAria", { name: model.name })}
                        onClick={() => setPreviewModel(model)}
                      >
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        aria-label={t("templates.actions.editAria", { name: model.name })}
                        onClick={() => handleOpenEdit(model)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setModelToDelete(model)}
                        aria-label={t("templates.actions.deleteAria", { name: model.name })}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(editingModel)}
        onOpenChange={(open) => {
          if (!open) {
            setEditingModel(null);
            setEditForm(initialForm);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-6xl">
          <DialogHeader>
            <DialogTitle>{t("templates.actions.editTitle")}</DialogTitle>
            <DialogDescription>{t("templates.actions.editDescription")}</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-model-name">{t("templates.modelName")}</Label>
              <Input
                id="edit-model-name"
                value={editForm.name}
                onChange={(event) => setEditForm((prev) => ({ ...prev, name: event.target.value }))}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button type="button" variant="outline" onClick={handleUseDefaultTemplateEdit}>
                {t("templates.actions.useDefault")}
              </Button>
            </div>

            <div className="grid max-w-full gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
              <div className="min-w-0 space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{t("templates.modelContent")}</Label>
                </div>
                <RichTextEditor
                  value={editForm.content}
                  onChange={(content) => setEditForm((prev) => ({ ...prev, content }))}
                  placeholder={t("templates.modelContentPlaceholder")}
                  insertTextRequest={editInsertRequest}
                  onInsertTextApplied={() => setEditInsertRequest(null)}
                />
              </div>

              <TokenPanel
                onInsertToken={(token) =>
                  setEditInsertRequest({ id: `${Date.now()}`, text: token })
                }
                categories={tokenCategories}
                disabledTokenKeys={editDisabledSignatureKeys}
              />
            </div>

            {editUnknownTokens.length > 0 ? (
              <p className="text-xs text-destructive">
                {t("templates.invalidTokens", { tokens: editUnknownTokens.map((token) => `{${token}}`).join(", ") })}
              </p>
            ) : null}

            <div className="flex items-center justify-between rounded-lg border p-3">
              <p className="text-sm font-medium">{t("templates.activeLabel")}</p>
              <Switch
                checked={editForm.is_active}
                onCheckedChange={(checked) => setEditForm((prev) => ({ ...prev, is_active: checked }))}
              />
            </div>

            <DialogFooter className="flex-row gap-2 sm:space-x-0">
              <Button
                type="button"
                variant="outline"
                className="w-full"
                onClick={() => setEditingModel(null)}
                disabled={isSavingEdit}
              >
                {t("templates.actions.close")}
              </Button>
              <Button
                type="button"
                className="w-full"
                onClick={handleSaveEdit}
                disabled={
                  isSavingEdit ||
                  !editForm.name.trim() ||
                  !stripRichTextHtml(editForm.content).trim() ||
                  editUnknownTokens.length > 0
                }
              >
                {isSavingEdit ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t("templates.actions.saving")}
                  </>
                ) : (
                  <>
                    <Plus className="h-4 w-4" />
                    {t("templates.actions.saveChanges")}
                  </>
                )}
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={Boolean(previewModel)}
        onOpenChange={(open) => {
          if (!open) {
            setPreviewModel(null);
          }
        }}
      >
        <DialogContent className="max-h-[90vh] max-w-5xl">
          <DialogHeader>
            <DialogTitle>{previewModel?.name ?? t("templates.previewFallbackTitle")}</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              {t("templates.previewDescription")}
            </DialogDescription>
          </DialogHeader>

          {previewModel && renderedPreview ? (
            <div className="space-y-4 sm:space-y-6">
              <div className="max-w-full overflow-auto rounded-lg border p-2">
                <TermPrintPreview
                  title={renderedPreview.title}
                  content={renderedPreview.content}
                  className="mx-auto w-[210mm] max-w-full p-4"
                />
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      <ConfirmationDialog
        open={Boolean(modelToDelete)}
        onOpenChange={(open) => {
          if (!open) setModelToDelete(null);
        }}
        title={t("templates.actions.deleteTitle")}
        description={t("templates.actions.deleteDescription", { title: modelToDelete?.name ?? "" })}
        confirmText={t("templates.actions.delete")}
        cancelText={t("templates.actions.cancel")}
        variant="destructive"
        onConfirm={async () => {
          if (!modelToDelete?.id) return;
          await deleteModel.mutateAsync(modelToDelete.id);
          setModelToDelete(null);
        }}
      />
    </div>
  );
};
